import { Input, type InputProps } from "@chakra-ui/react";
import {
  lte24,
  timeToNumber,
  validateTime,
} from "@src/components/ui-kit/TimeInput/utils";
import { formatDuration } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { type ChangeEvent, forwardRef, useMemo, useState } from "react";

type TimeInputProps = {
  /** Value in seconds */
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  mode24?: boolean;
} & Omit<InputProps, "value" | "onChange">;

const TimeInput = forwardRef<HTMLInputElement | null, TimeInputProps>(
  function TimeInput(
    { value, onChange, onFocus, mode24, isDisabled, onBlur, ...props },
    inputRef,
  ) {
    const [lastValue, setLastValue] = useState<[string, number]>(["", 0]);

    const textValue = useMemo(() => {
      if (value === undefined) return "";
      return formatDuration(value);
    }, [value]);

    const _onChange = (e: ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (val === "") onChange(undefined);
      if (!validateTime(val)) return;

      const time = timeToNumber(val, { mode24 });
      if (mode24 && !lte24(time)) return;

      setLastValue([val, time]);
      onChange(time);
    };

    const _onBlur = () => {
      setLastValue((val) => [formatDuration(val[1]), val[1]]);
    };

    return (
      <Input
        ref={inputRef}
        textAlign="center"
        autoComplete="off"
        inputMode="numeric"
        isDisabled={isDisabled}
        onBlur={(e) => {
          _onBlur();
          onBlur?.(e);
        }}
        onChange={_onChange}
        onFocus={(e) => {
          e.target.select();
          onFocus?.(e);
        }}
        placeholder="00:00"
        type="text"
        value={value === lastValue[1] ? lastValue[0] : textValue}
        {...props}
      />
    );
  },
);

export default observer(TimeInput);
